import { Typography, Grid } from '@mui/material';

import { use } from 'react';

import { graphql, type DocumentType } from '@/gql';
import { UserAvatar } from '@/modules/user/components/UserAvatar';
import { TimeAgo } from '@/components/date-time/TimeAgo';
import { Editor } from '@/modules/editor/Editor';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { getAuthorName } from '../../noticeboardUtils';

import { PostCommentDeleteButton } from './comment/PostCommentDeleteButton';

const POST_COMMENTS_COMMENT_FRAGMENT = graphql(`
  fragment postCommentsCommentFields on PostComment {
    ...postCommentDeleteButtonCommentFragment
    _id
    htmlContent
    editorContent
    content
    created
    author {
      ...authorData
    }
    authorContext
  }
`);

const POST_COMMENTS_POST_FRAGMENT = graphql(`
  fragment postCommentsPostFields on Post {
    ...postCommentDeleteButtonPostFragment
  }
`);

const POST_COMMENTS_GROUP_FRAGMENT = graphql(`
  fragment postCommentsGroupFields on Group {
    _id
    userCanDeleteAllPosts
  }
`);

interface Props {
  comment: DocumentType<typeof POST_COMMENTS_COMMENT_FRAGMENT>;
  post: DocumentType<typeof POST_COMMENTS_POST_FRAGMENT>;
  group: DocumentType<typeof POST_COMMENTS_GROUP_FRAGMENT>;
}

export const PostComment = ({ comment, post, group }: Props) => {
  const { currentUserId } = use(CurrentUserContext);

  const { htmlContent, editorContent, content, created, author } =
    comment ?? {};
  const { _id: authorId } = author ?? {};
  const { userCanDeleteAllPosts } = group ?? {};

  const authorName = getAuthorName(comment);

  const canDeleteComment = authorId === currentUserId || userCanDeleteAllPosts;

  return (
    <Grid container sx={{ width: '100%' }}>
      <Grid
        size={{
          xs: 2,
          sm: 1,
        }}
      >
        <UserAvatar user={author} size={40} />
      </Grid>
      <Grid
        size={{
          xs: 9,
          sm: 10,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: 16 }}>
          {authorName}
        </Typography>
        <Editor
          htmlContent={htmlContent}
          draftJsContent={editorContent}
          stringContent={content}
        />
        <TimeAgo date={created} />
      </Grid>
      <Grid
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
        }}
        size={1}
      >
        {canDeleteComment && (
          <PostCommentDeleteButton comment={comment} post={post} />
        )}
      </Grid>
    </Grid>
  );
};
